import { useState, useCallback } from 'react'
import { DndContext, useDraggable, useDroppable, DragOverlay } from '@dnd-kit/core'
import type { DragEndEvent, DragStartEvent } from '@dnd-kit/core'
import { CSS } from '@dnd-kit/utilities'
import { Trash2, GripVertical, CheckCircle2 } from 'lucide-react'
import ToolWrapper, { type ToolHostProps, type ToolResultItem } from '../../components/prioritization/ToolWrapper'
import { type ToolItem } from '../../contexts/toolItemsHooks'
import { useLiveToolSession } from '../../components/prioritization/session/liveExperience/useLiveToolSession'
import { EisenhowerLiveView } from './EisenhowerLiveView'

// null = uncategorized
type QuadrantKey = 'ui' | 'in' | 'un' | 'nn' | null

interface TaskItem {
  id: string
  title: string
  description?: string
  quadrant: QuadrantKey
  sourceId?: string // Reference to original item from workspace
}

const quadrantMeta: Record<Exclude<QuadrantKey, null>, { 
  title: string
  subtitle: string
  action: string
  color: string
  border: string
  headerBg: string
}> = {
  ui: { 
    title: 'Urgent & Important', 
    subtitle: 'Crisis, deadlines, problems',
    action: 'DO',
    color: 'bg-[var(--coral-dim)]', 
    border: 'border-[var(--coral)]',
    headerBg: 'bg-[var(--coral-dim)]'
  },
  in: { 
    title: 'Important, Not Urgent', 
    subtitle: 'Prevention, planning, improvement',
    action: 'SCHEDULE',
    color: 'bg-[var(--green-dim)]', 
    border: 'border-[var(--green)]',
    headerBg: 'bg-[var(--green-dim)]'
  },
  un: { 
    title: 'Urgent, Not Important', 
    subtitle: 'Interruptions, some meetings',
    action: 'DELEGATE',
    color: 'bg-[var(--mustard-dim)]', 
    border: 'border-[var(--mustard)]',
    headerBg: 'bg-[var(--mustard-dim)]'
  },
  nn: { 
    title: 'Not Urgent & Not Important', 
    subtitle: 'Time wasters, pleasant activities',
    action: 'ELIMINATE',
    color: 'bg-[var(--surface)]', 
    border: 'border-[var(--rule)]',
    headerBg: 'bg-[var(--hover-tint)]'
  }
}

// Draggable item card
function DraggableCard({
  item,
  onRemove,
  onMove,
}: {
  item: TaskItem
  onRemove: (id: string) => void
  onMove: (id: string, quadrant: QuadrantKey) => void
}) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: item.id,
  })
  
  const style = transform ? {
    transform: CSS.Translate.toString(transform),
  } : undefined

  return (
    <div 
      ref={setNodeRef} 
      style={style}
      className={`p-3 bg-[var(--bg-raised)] border border-[var(--rule)]  shadow-sm flex items-start gap-2 cursor-grab active:cursor-grabbing ${isDragging ? 'opacity-50 shadow-lg ring-2 ring-blue-400' : ''}`}
    >
      {/* touch-none: without it a finger on the handle scrolls the page instead of dragging */}
      <div {...listeners} {...attributes} className="touch-none p-2 -m-2 text-[var(--text-ter)] hover:text-[var(--text-sec)]">
        <GripVertical className="w-4 h-4" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-medium text-sm text-[var(--text-pri)]">{item.title}</div>
        {item.description && <div className="text-xs text-[var(--text-ter)] mt-1 line-clamp-2">{item.description}</div>}
        {/* On a phone the quadrants sit a screen away from the item, so dragging is
            not the only way: pick the quadrant here. */}
        <select
          aria-label={`Move ${item.title} to`}
          value={item.quadrant ?? ''}
          onChange={(e) => onMove(item.id, (e.target.value || null) as QuadrantKey)}
          className="md:hidden mt-2 w-full border border-[var(--rule)] bg-[var(--bg-raised)] px-2 py-2 text-sm text-[var(--text-pri)]"
        >
          <option value="">Not categorized</option>
          {(Object.keys(quadrantMeta) as Exclude<QuadrantKey, null>[]).map((q) => (
            <option key={q} value={q}>{quadrantMeta[q].title}</option>
          ))}
        </select>
      </div>
      <button 
        onClick={(e) => { e.stopPropagation(); onRemove(item.id); }} 
        className="text-[var(--text-ter)] hover:text-[var(--coral)] p-1" 
        aria-label="Remove item"
      >
        <Trash2 className="w-3.5 h-3.5" />
      </button>
    </div>
  )
}

// Static card for overlay during drag
function CardOverlay({ item }: { item: TaskItem }) {
  return (
    <div className="p-3 bg-[var(--bg-raised)] border-2 border-[var(--slate)] shadow-xl flex items-start gap-2 cursor-grabbing">
      <div className="text-[var(--text-ter)]">
        <GripVertical className="w-4 h-4" />
      </div>
      <div className="flex-1">
        <div className="font-medium text-sm text-[var(--text-pri)]">{item.title}</div>
      </div>
    </div>
  )
}

// Droppable quadrant zone
function QuadrantZone({ 
  quadrantKey, 
  items, 
  onRemove,
  onMove,
}: { 
  quadrantKey: Exclude<QuadrantKey, null>
  items: TaskItem[]
  onRemove: (id: string) => void
  onMove: (id: string, quadrant: QuadrantKey) => void
}) {
  const { setNodeRef, isOver } = useDroppable({
    id: `quadrant-${quadrantKey}`,
  })
  
  const meta = quadrantMeta[quadrantKey]
  
  return (
    <div 
      ref={setNodeRef}
      className={`${meta.color} border-2 ${isOver ? 'border-[var(--slate)] ring-2 ring-blue-200' : meta.border}  p-3 min-h-[200px] transition-all`}
    >
      <div className={`${meta.headerBg} -mx-3 -mt-3 px-3 py-2  mb-3`}>
        <div className="flex items-center justify-between">
          <div>
            <div className="font-semibold text-[var(--text-pri)] text-sm">{meta.title}</div>
            <div className="text-xs text-[var(--text-sec)]">{meta.subtitle}</div>
          </div>
          <div className="text-xs font-bold text-[var(--text-sec)] bg-[var(--bg-raised)]/50 px-2 py-1 rounded">
            {meta.action}
          </div>
        </div>
      </div>
      <div className="space-y-2 min-h-[120px]">
        {items.length === 0 && (
          <div className={`text-sm text-[var(--text-ter)] italic text-center py-8 border-2 border-dashed  ${isOver ? 'border-[var(--slate)] bg-[var(--slate-dim)]' : 'border-[var(--rule)]'}`}>
            Drop items here
          </div>
        )}
        {items.map(item => (
          <DraggableCard key={item.id} item={item} onRemove={onRemove} onMove={onMove} />
        ))}
      </div>
      {items.length > 0 && (
        <div className="mt-2 text-xs text-[var(--text-ter)] text-right">
          {items.length} item{items.length !== 1 ? 's' : ''}
        </div>
      )}
    </div>
  )
}

// Uncategorized items area
function UncategorizedZone({ items, onRemove, onMove }: { items: TaskItem[]; onRemove: (id: string) => void; onMove: (id: string, quadrant: QuadrantKey) => void }) {
  const { setNodeRef, isOver } = useDroppable({
    id: 'quadrant-uncategorized',
  })
  
  if (items.length === 0) return null
  
  return (
    <div 
      ref={setNodeRef}
      className={`border-2 ${isOver ? 'border-[var(--slate)] bg-[var(--slate-dim)]' : 'border-[var(--slate)] bg-[var(--slate-dim)]'}  p-4 transition-all`}
    >
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="font-semibold text-[var(--text-ter)]">Items to Categorize</h3>
          <p className="text-xs text-[var(--text-ter)]">Drag each item to the appropriate quadrant</p>
        </div>
        <span className="text-sm font-medium text-[var(--text-sec)] bg-[var(--slate-dim)] px-2 py-1 rounded">
          {items.length} remaining
        </span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
        {items.map(item => (
          <DraggableCard key={item.id} item={item} onRemove={onRemove} onMove={onMove} />
        ))}
      </div>
    </div>
  )
}

function EisenhowerMatrixContent(props: ToolHostProps) {
  const { isLiveSession, completeLabel } = useLiveToolSession()
  const [items, setItems] = useState<TaskItem[]>([])
  const [activeId, setActiveId] = useState<string | null>(null)

  const uncategorizedItems = items.filter(i => i.quadrant === null)
  const tasksByQuadrant = (q: Exclude<QuadrantKey, null>) => items.filter(i => i.quadrant === q)

  const handleRemove = (id: string) => setItems(prev => prev.filter(i => i.id !== id))
  const handleMove = (id: string, quadrant: QuadrantKey) =>
    setItems(prev => prev.map(i => i.id === id ? { ...i, quadrant } : i))
  
  // Handler for importing items from workspace - start uncategorized
  const handleImportItems = useCallback((toolItems: ToolItem[]) => {
    const newTasks: TaskItem[] = toolItems.map(item => ({
      id: item.id,
      title: item.name,
      description: item.description,
      quadrant: null, // Start uncategorized
      sourceId: item.sourceId,
    }))
    setItems(prev => [...prev, ...newTasks])
  }, [])
  
  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(String(event.active.id))
  }

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    setActiveId(null)
    
    if (!over) return

    const activeItem = items.find(i => i.id === active.id)
    if (!activeItem) return

    const overId = String(over.id)
    
    // Determine target quadrant from drop zone
    if (overId === 'quadrant-uncategorized') {
      setItems(prev => prev.map(i => i.id === activeItem.id ? { ...i, quadrant: null } : i))
    } else if (overId.startsWith('quadrant-')) {
      const quadrant = overId.replace('quadrant-', '') as Exclude<QuadrantKey, null>
      setItems(prev => prev.map(i => i.id === activeItem.id ? { ...i, quadrant } : i))
    }
  }

  // Get results for saving as session result
  // Priority order: ui (Do) > in (Schedule) > un (Delegate) > nn (Eliminate)
  const handleGetResults = useCallback((): ToolResultItem[] => {
    const quadrantPriority: Record<Exclude<QuadrantKey, null>, number> = {
      'ui': 1, // Urgent & Important - highest priority
      'in': 2, // Important, Not Urgent
      'un': 3, // Urgent, Not Important
      'nn': 4, // Not Urgent & Not Important - lowest priority
    }
    
    return items
      .filter(i => i.quadrant !== null)
      .map(i => ({
        id: i.sourceId || i.id,
        name: i.title,
        category: i.quadrant!,
        score: 5 - quadrantPriority[i.quadrant!], // Convert to score (4, 3, 2, 1)
        classificationBreakdown: {
          quadrant: i.quadrant!,
          urgency: i.quadrant === 'ui' || i.quadrant === 'un' ? 'high' : 'low',
          importance: i.quadrant === 'ui' || i.quadrant === 'in' ? 'high' : 'low',
        },
        metadata: {
          urgency: i.quadrant === 'ui' || i.quadrant === 'un' ? 'high' : 'low',
          importance: i.quadrant === 'ui' || i.quadrant === 'in' ? 'high' : 'low',
          action: quadrantMeta[i.quadrant!].action,
        }
      }))
      .sort((a, b) => (b.score || 0) - (a.score || 0)) // Higher score first
  }, [items])

  const activeItem = activeId ? items.find(i => i.id === activeId) : null
  const allCategorized = items.length > 0 && uncategorizedItems.length === 0
  return (
    <ToolWrapper
      toolName="Eisenhower Matrix"
      toolDescription="Categorize items by urgency and importance"
      onImportItems={handleImportItems}
      onGetResults={allCategorized ? handleGetResults : undefined}
      itemCount={items.length}
      {...props}
    >
      {isLiveSession ? (
        <EisenhowerLiveView
          items={items}
          activeId={activeId}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        />
      ) : (
      <div className="space-y-6">
        
        <DndContext onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
          {/* Uncategorized items */}
          <UncategorizedZone items={uncategorizedItems} onRemove={handleRemove} onMove={handleMove} />
          
          {/* Summary bar when items exist */}
          {items.length > 0 && (
            <div className="flex items-center justify-between p-3 bg-[var(--surface)] border">
              <div className="flex items-center gap-4">
                <span className="font-semibold text-[var(--text-pri)]">{items.length} total items</span>
                <span className="text-[var(--text-ter)]">|</span>
                <span className={uncategorizedItems.length > 0 ? 'text-[var(--mustard)]' : 'text-[var(--green)] dark:text-[var(--text-ter)]'}>
                  {uncategorizedItems.length > 0 
                    ? `${uncategorizedItems.length} uncategorized`
                    : 'All items categorized'}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setItems([])}
                  className="text-sm text-[var(--text-ter)] hover:text-[var(--coral)]"
                >
                  Clear All
                </button>
              </div>
            </div>
          )}

          {/* Matrix */}
          {items.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Top row: Important */}
              <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-4">
                <QuadrantZone quadrantKey="ui" items={tasksByQuadrant('ui')} onRemove={handleRemove} onMove={handleMove} />
                <QuadrantZone quadrantKey="in" items={tasksByQuadrant('in')} onRemove={handleRemove} onMove={handleMove} />
              </div>
              {/* Bottom row: Not Important */}
              <QuadrantZone quadrantKey="un" items={tasksByQuadrant('un')} onRemove={handleRemove} onMove={handleMove} />
              <QuadrantZone quadrantKey="nn" items={tasksByQuadrant('nn')} onRemove={handleRemove} onMove={handleMove} />
            </div>
          )}
          
          {/* Drag overlay for smooth dragging */}
          <DragOverlay>
            {activeItem ? <CardOverlay item={activeItem} /> : null}
          </DragOverlay>
        </DndContext>
        
        {/* Completion prompt */}
        {allCategorized && (
          <div className="card bg-[var(--green-dim)] border-[var(--green)]">
            <div className="flex items-center gap-3">
              <CheckCircle2 className="h-6 w-6 text-[var(--green)] flex-shrink-0" />
              <div>
                <h3 className="font-semibold text-[var(--green)] dark:text-[var(--text-ter)]">All items categorized!</h3>
                <p className="text-sm text-[var(--green)] dark:text-[var(--text-ter)]">
                  Click <strong>"{completeLabel}"</strong> in the header to create a ranked session result with visible downstream context.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
      )}
    </ToolWrapper>
  )
}

export default function EisenhowerMatrix(props: ToolHostProps) {
  return <EisenhowerMatrixContent {...props} />
}


